import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, Share2, Loader2, FileText } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import ComprehensiveAnalysisResults from '@/components/ComprehensiveAnalysisResults';
import ScrollToTop from '@/components/ScrollToTop';
import BestOfThreeResults from '@/components/BestOfThreeResults';
import RatingFeedback from '@/components/RatingFeedback';
import AIEnhancement from '@/components/AIEnhancement';
import ShareButton from '@/components/ShareButton';
import { generateSingleAnalysisWord } from '@/utils/wordGenerator';
import { generatePDFFromHTML } from '@/utils/pdfGenerator';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AnalysisDetail = () => {
  const { analysisId } = useParams();
  const navigate = useNavigate();
  const { user, authToken, checkAuth } = useAuth();
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('darkMode');
    return saved ? JSON.parse(saved) : false;
  });

  useEffect(() => {
    fetchAnalysis();
  }, [analysisId]);

  const fetchAnalysis = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/analyses/${analysisId}`, {
        withCredentials: true
      });
      
      const analysisData = response.data;
      
      // Check if this is a comparison/Compare analysis
      if (analysisData.is_comparison && analysisData.comparison_group_id) {
        console.log('📊 Comparison detected - fetching all related analyses');
        
        // Fetch all analyses in this comparison group
        try {
          const allAnalysesResponse = await axios.get(`${API}/analyses?limit=100`, {
            withCredentials: true
          });
          
          // Filter to get all analyses with the same comparison_group_id
          const comparisonAnalyses = allAnalysesResponse.data.filter(
            a => a.comparison_group_id === analysisData.comparison_group_id
          );
          
          console.log(`Found ${comparisonAnalyses.length} analyses in comparison group`);
          
          // Sort by some criteria (e.g., overall score or index if available)
          comparisonAnalyses.sort((a, b) => (b.valence || 0) - (a.valence || 0));
          
          // Transform into the format expected by BestOfThreeResults
          const formattedResults = comparisonAnalyses.map((a, idx) => ({
            ...a,
            imageIndex: idx,
            preview: a.image_data,
            user_purpose: a.custom_question || 'Image comparison',
            overallScore: ((a.valence || 0) + (a.arousal_level || 0) + (a.dominance || 0)) * 100 / 3
          }));
          
          // Set analysis with all_results for BestOfThreeResults
          setAnalysis({
            ...analysisData,
            is_best_of_three: true,
            all_results: formattedResults
          });
        } catch (groupErr) {
          console.error('Error fetching comparison group:', groupErr);
          // Fallback to single analysis
          setAnalysis(analysisData);
        }
      } else {
        setAnalysis(analysisData);
      }
      
      setError(null);
    } catch (err) {
      console.error('Error fetching analysis:', err);
      setError('Failed to load analysis. It may have been deleted or you may not have access.');
      toast.error('Failed to load analysis');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadWord = async () => {
    if (!analysis) {
      toast.error('No analysis to download');
      return;
    }
    
    try {
      await generateSingleAnalysisWord(analysis);
      toast.success('Word report downloaded successfully!');
    } catch (error) {
      console.error('Download error:', error);
      toast.error(`Failed to generate report: ${error.message}`);
    }
  };

  const handleDownloadPDF = async () => {
    if (!analysis) {
      toast.error('No analysis to download');
      return;
    }
    
    try {
      // Check if element exists
      const element = document.getElementById('analysis-results-pdf');
      if (!element) {
        throw new Error('Analysis results not found. Please wait for the page to fully load.');
      }
      
      toast.loading('Generating PDF...', { id: 'pdf-gen' });
      
      // Small delay to ensure all content is rendered
      await new Promise(resolve => setTimeout(resolve, 500));
      
      await generatePDFFromHTML('analysis-results-pdf', `emoQ-analysis-${analysis.content_type}`);
      toast.success('PDF report downloaded successfully!', { id: 'pdf-gen' });
    } catch (error) {
      console.error('PDF download error:', error);
      toast.error(`Failed to generate PDF: ${error.message}`, { id: 'pdf-gen' });
    }
  };

  const toggleDarkMode = () => {
    const newMode = !darkMode;
    setDarkMode(newMode);
    localStorage.setItem('darkMode', JSON.stringify(newMode));
  };

  if (loading) {
    return (
      <div className={`min-h-screen ${darkMode ? 'bg-gray-900' : 'bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50'}`}>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <Loader2 className={`w-12 h-12 animate-spin mx-auto mb-4 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} />
            <p className={darkMode ? 'text-gray-300' : 'text-gray-600'}>Loading analysis...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !analysis) {
    return (
      <div className={`min-h-screen ${darkMode ? 'bg-gray-900' : 'bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50'}`}>
        <div className="flex items-center justify-center min-h-screen p-4">
          <div className="text-center max-w-md">
            <p className={`mb-4 ${darkMode ? 'text-red-400' : 'text-red-600'}`}>
              {error || 'Analysis not found'}
            </p>
            <button 
              onClick={() => navigate('/history')}
              className="px-6 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700"
            >
              Back to My Analyses
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-gray-900' : 'bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50'}`}>
      {/* Header */}
      <header className={`border-b ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white/80 border-blue-100'} backdrop-blur-sm sticky top-0 z-50`}>
        <div className="max-w-7xl mx-auto px-4 py-3 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/history')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                darkMode 
                  ? 'hover:bg-gray-700 text-gray-300' 
                  : 'hover:bg-gray-100 text-gray-700'
              }`}
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Back</span>
            </button>
            <h1 className={`text-xl font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              Analysis Details
            </h1>
          </div>
          
          <button
            onClick={toggleDarkMode}
            className={`p-2 rounded-full transition-colors ${
              darkMode ? 'hover:bg-gray-700 text-yellow-400' : 'hover:bg-gray-100 text-gray-700'
            }`}
          >
            {darkMode ? '☀️' : '🌙'}
          </button>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Analysis Metadata */}
        <div className={`mb-6 p-4 rounded-lg shadow-lg ${darkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'}`}>
          <div className="flex flex-wrap gap-4 items-center justify-between">
            <div>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                <span className="font-semibold">Type:</span> {analysis.content_type?.toUpperCase()}
                {' • '}
                <span className="font-semibold">Date:</span> {new Date(analysis.timestamp).toLocaleString()}
              </p>
              {analysis.custom_question && (
                <p className={`text-sm mt-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  <span className="font-semibold">Question:</span> {analysis.custom_question}
                </p>
              )}
            </div>
            
            <div className="flex flex-wrap gap-2">
              <ShareButton analysisId={analysis.id} userName={analysis.user_name || "User"} />
              <button
                onClick={handleDownloadPDF}
                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-red-600 to-pink-600 text-white rounded-lg hover:from-red-700 hover:to-pink-700 transition-all"
              >
                <FileText className="w-4 h-4" />
                <span>Download PDF</span>
              </button>
              <button
                onClick={handleDownloadWord}
                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg hover:from-green-700 hover:to-emerald-700 transition-all"
              >
                <Download className="w-4 h-4" />
                <span>Download Word</span>
              </button>
            </div>
          </div>
        </div>

        {/* Original Content Display */}
        {analysis.image_data && (
          <div className={`mb-6 p-4 rounded-lg shadow-lg ${darkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'}`}>
            <h2 className={`text-lg font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              Analyzed Content
            </h2>
            <img 
              src={analysis.image_data} 
              alt="Analyzed content" 
              className="w-full max-w-2xl mx-auto rounded-lg"
            />
          </div>
        )}

        {analysis.content_type === 'text' && analysis.content_preview && (
          <div className={`mb-6 p-4 rounded-lg shadow-lg ${darkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'}`}>
            <h2 className={`text-lg font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              Analyzed Text
            </h2>
            <p className={`whitespace-pre-wrap ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              {analysis.content_preview}
            </p>
          </div>
        )}

        {analysis.content_type === 'url' && (
          <div className={`mb-6 p-4 rounded-lg shadow-lg ${darkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'}`}>
            <h2 className={`text-lg font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              Analyzed URL
            </h2>
            <a 
              href={analysis.content} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline break-all"
            >
              {analysis.content}
            </a>
            {analysis.content_description && (
              <p className={`mt-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                {analysis.content_description}
              </p>
            )}
          </div>
        )}

        {/* Comprehensive Analysis Results - Wrapped for PDF Generation */}
        <div id="analysis-results-pdf">
          {/* Content Context Section - For PDF/Export */}
          <div className={`mb-6 p-4 rounded-lg ${darkMode ? 'bg-gray-800 border border-gray-700' : 'bg-gray-50 border border-gray-200'}`}>
            <h3 className={`text-lg font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              Analysis Context
            </h3>
            <div className={`space-y-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              <p><span className="font-semibold">Content Type:</span> {analysis.content_type?.toUpperCase()}</p>
              <p><span className="font-semibold">Date:</span> {new Date(analysis.timestamp).toLocaleString()}</p>
              
              {analysis.content && analysis.content_type !== 'video' && (
                <div>
                  <span className="font-semibold">Original Content:</span>
                  <p className="mt-1 p-3 rounded bg-opacity-50 bg-gray-600 whitespace-pre-wrap break-words">
                    {analysis.content_type === 'url' ? (
                      <a href={analysis.content} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">
                        {analysis.content}
                      </a>
                    ) : (
                      analysis.content.length > 500 ? `${analysis.content.substring(0, 500)}...` : analysis.content
                    )}
                  </p>
                </div>
              )}
              
              {analysis.custom_question && (
                <div>
                  <span className="font-semibold">User's Question/Instructions:</span>
                  <p className="mt-1 p-3 rounded bg-opacity-50 bg-gray-600 italic">
                    "{analysis.custom_question}"
                  </p>
                </div>
              )}
              
              {analysis.custom_answer && (
                <div>
                  <span className="font-semibold">AI Response:</span>
                  <p className="mt-1 p-3 rounded bg-opacity-50 bg-gray-600">
                    {analysis.custom_answer}
                  </p>
                </div>
              )}
              
              {analysis.audience_filters && Object.keys(analysis.audience_filters).length > 0 && (
                <div>
                  <span className="font-semibold">Target Audience:</span>
                  <p className="mt-1">
                    {analysis.audience_filters.locations?.length > 0 && `Locations: ${analysis.audience_filters.locations.join(', ')}. `}
                    {analysis.audience_filters.demographics?.length > 0 && `Demographics: ${analysis.audience_filters.demographics.join(', ')}. `}
                    {analysis.audience_filters.professions?.length > 0 && `Professions: ${analysis.audience_filters.professions.join(', ')}.`}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Check if this is Compare results */}
          {analysis.is_best_of_three && analysis.all_results ? (
            /* Compare Results Display */
            <BestOfThreeResults
              results={analysis.all_results}
              darkMode={darkMode}
              user={user}
              authToken={authToken}
            />
          ) : (
            /* Standard Analysis Results */
            <>
              <ComprehensiveAnalysisResults result={analysis} darkMode={darkMode} />
              
              {/* AI Enhancement Section - Inside PDF container */}
              <div className="mt-8">
                <AIEnhancement 
                  analysis={analysis} 
                  darkMode={darkMode}
                  user={user}
                  authToken={authToken}
                  onCreditsUpdate={checkAuth}
                />
              </div>
            </>
          )}
        </div>

        {/* Rating & Feedback Section - Outside PDF */}
        <div className="mt-8">
          <RatingFeedback analysisId={analysis.id} darkMode={darkMode} />
        </div>

        {/* Action Buttons at Bottom */}
        <div className={`mt-8 p-6 rounded-lg shadow-lg ${darkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'}`}>
          <h3 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            Actions
          </h3>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all font-semibold"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>New Analysis</span>
            </button>
            <ShareButton analysisId={analysis.id} userName={analysis.user_name || "User"} />
            <button
              onClick={handleDownloadPDF}
              className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-red-600 to-pink-600 text-white rounded-lg hover:from-red-700 hover:to-pink-700 transition-all font-semibold"
            >
              <FileText className="w-5 h-5" />
              <span>Download PDF</span>
            </button>
            <button
              onClick={handleDownloadWord}
              className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg hover:from-green-700 hover:to-emerald-700 transition-all font-semibold"
            >
              <Download className="w-5 h-5" />
              <span>Download Word</span>
            </button>
          </div>
        </div>
      </div>
      
      {/* Scroll to Top Button */}
      <ScrollToTop darkMode={darkMode} />
    </div>
  );
};

export default AnalysisDetail;
